/**
 * @providesModule FiltersList
 * @flow
 */

import React, { PropTypes } from 'react'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'

import { Button, Intent } from '@blueprintjs/core'

import Filter from '../Filter'
import FilterImport from './FilterImport'
import shopifyGlyph from '../../shopify_glyph_black.svg'
import { ratingText } from '../../shared/ratingText'
import { createFilter, openShopifyProduct } from '../../redux/actions'

import type { ContextRouter } from 'react-router-dom'

import type { Dispatch } from '../../redux/actions'
import type { State, Filter as FilterT } from '../../redux/state'

type OwnProps = {
  ...ContextRouter,
  matchingFilterIds: ?string[]
}

function mapStateToProps(state: State, { matchingFilterIds }: OwnProps) {
  return {
    filters: Object.keys(state.filtersById)
      .filter(id => matchingFilterIds == null || matchingFilterIds.includes(id))
      .map(x => state.filtersById[x])
      .sort((a, b) => a.name.localeCompare(b.name))
  }
}

function mapDispatchToProps(dispatch: Dispatch, { history }: OwnProps) {
  return {
    handleAddFilter: () => {
      dispatch(createFilter()).then(id => history.replace(`/filters/${id}`))
    },
    handleOpenShopify: (id: string) => dispatch(openShopifyProduct(id))
  }
}

type Props = OwnProps & { handleAddFilter: () => {} } & {
  filters: FilterT[],
  handleOpenShopify: (id: string) => Promise<mixed>
}
function FiltersList({
  filters,
  match,
  handleAddFilter,
  handleOpenShopify,
  matchingFilterIds
}: Props) {
  return (
    <article style={{ marginTop: '2em' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h4>Filter Specifications</h4>
        <div>
        <FilterImport />
        <Button
          intent={Intent.SUCCESS}
          iconName="add"
          text="New Filter"
          onClick={handleAddFilter}
        />
        </div>
      </div>
      {matchingFilterIds != null && (
        <div style={{ marginBottom: 15 }}>
          <p role="status">{filters.length ? `${filters.length} matching filters` : 'No matching filters'}</p>
        </div>
      )}
      <table className="pt-table pt-interactive" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Name</th>
            <th aria-label="Shopify" style={{ width: 32 }} />
            <th>CE</th>
            <th>VLT</th>
            <th>Price</th>
            <th>OD Ranges</th>
            <th>CE Ratings</th>
          </tr>
        </thead>
        <tbody>
          {filters.map(filter => (
            <FiltersListEntry
              key={filter.id}
              {...filter}
              handleOpenShopify={handleOpenShopify}
            />
          ))}
        </tbody>
      </table>

      <Route path={`${match.url}/:filterId`} component={Filter} />
    </article>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(FiltersList)

function FiltersListEntry({
  id,
  name,
  ce,
  vlt,
  color,
  basePrice,
  ods,
  ceRatings,
  shopifyProduct,
  handleOpenShopify
}) {
  return (
    <Route>
      {({ history, match }) => (
        <tr onClick={() => history.replace(`${match.url}/${id}`)}>
          <td>
            <span className="pt-tag pt-minimal">{name || '—'}</span>
          </td>
          <td style={{ width: 32, paddingRight: 0 }}>
            {shopifyProduct && (
              <button
                type="button"
                aria-label="Open in Shopify"
                title="Open in Shopify"
                onClick={event => {
                  event.stopPropagation()
                  handleOpenShopify(id)
                }}
                style={{
                  display: 'block',
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer'
                }}
              >
                <img
                  src={shopifyGlyph}
                  alt=""
                  style={{
                    display: 'block',
                    width: 16,
                    height: 19,
                    opacity: 0.6
                  }}
                />
              </button>
            )}
          </td>
          <td>{ce ? 'Certified' : 'Pending'}</td>
          <td>{`${vlt || '—'}% ${color || '—'}`}</td>
          <td>
            {`$${
              parseFloat(basePrice) % 1
                ? parseFloat(basePrice).toFixed(2)
                : parseInt(basePrice, 10) || '—'
            }`}
          </td>
          <td>
            <RangeList items={ods} />
          </td>
          <td>
            <RangeList items={ceRatings} ce />
          </td>
        </tr>
      )}
    </Route>
  )
}

const RangeList = ({ items = [], ce = false }) => (
  <p
    style={{ maxWidth: '30em', margin: 0, whiteSpace: 'pre-wrap' }}
    className="pt-text-overflow-ellipsis"
  >
    {items.map((item, i) => (
      <span key={i}>
        {i === 0 ? '' : <span className="pt-text-muted"> | </span>}
        {item.value ? <>{item.range} <strong>{item.value}</strong></> : ratingText(item, ce)}
      </span>
    ))}
  </p>
)
